/**
 * Email templates with {{field}} merge notation.
 * All templates produce { subject, body } strings.
 */

/**
 * Merge {{field}} placeholders (and {{#if field}}...{{/if}} blocks) against
 * a data object. {{#if}} blocks are resolved first so a nested {{field}}
 * inside one still gets substituted by the second pass.
 */
function merge(template, data) {
  const withConditionals = template.replace(
    /\{\{#if (\w+(?:\.\w+)*)\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_, key, inner) => {
      const val = key.split('.').reduce((o, k) => o?.[k], data);
      return val ? inner : '';
    }
  );
  return withConditionals.replace(/\{\{(\w+(?:\.\w+)*)\}\}/g, (_, key) => {
    const val = key.split('.').reduce((o, k) => o?.[k], data);
    return val != null ? String(val) : `[${key}]`;
  });
}

/**
 * The checklist items a speaker (not the chapter) is expected to supply,
 * checked against the engagement's actual checklist state to build a
 * "what's still outstanding" summary for reminder templates.
 */
const SPEAKER_SUPPLIED_ITEMS = [
  { key: 'title', label: 'Talk title' },
  { key: 'abstract', label: 'Session abstract' },
  { key: 'biography', label: 'Speaker biography' },
  { key: 'headshot', label: 'Headshot photo' },
  { key: 'agreement-rcvd', label: 'Signed speaker agreement' },
];

/** Bullet list of not-yet-completed speaker-supplied items, or null if none are outstanding. */
function buildMissingItemsList(engagement) {
  const checklist = engagement?.checklist ?? {};
  const missing = SPEAKER_SUPPLIED_ITEMS.filter(({ key }) => !checklist[key]?.completed);
  if (missing.length === 0) return null;
  return missing.map(({ label }) => `• ${label}`).join('\n');
}

/**
 * `a ?? b` only falls through on null/undefined — a real record with an
 * empty-string field (e.g. firstName: "") sails right past it and leaves a
 * blank in the merged email ("Dear ,"). Treat blank/whitespace-only strings
 * as missing too.
 */
function orDefault(value, fallback) {
  if (typeof value === 'string' && value.trim() === '') return fallback;
  return value ?? fallback;
}

/**
 * Who the email is from — the actual signed-in user and their chapter, so
 * the sign-off reads "Best regards, Jane Doe / PMI Greater Boston Chapter"
 * instead of a literal "[Your Name]" placeholder. Falls back to the app's
 * own name (not a bracket placeholder) when no organization record exists
 * yet — a signature line should never look broken in a real, sent email.
 */
function buildSenderContext(sender) {
  return {
    senderName: orDefault(sender?.name, orDefault(sender?.email, '[Your Name]')),
    chapterName: orDefault(sender?.chapterName, 'Speaker Engagement Tracker'),
  };
}

/** Flatten engagement + event + speaker (+ sender) into a single merge context */
export function buildContext(engagement, event, speaker, sender) {
  const speakerName = speaker
    ? `${orDefault(speaker.preferredName, orDefault(speaker.firstName, '[Speaker]'))} ${orDefault(speaker.lastName, '')}`.trim()
    : '[Speaker Name]';
  // A single computed line for the optional PDU block — {{#if}} only gates
  // presence/absence of the whole field, so if pduInfo text existed but
  // pduCode alone were missing, "(Code: {{pduCode}})" would render as a
  // dangling "(Code: )" in a real sent email. Composing the whole line here
  // means there's nothing left to go missing once pduInfo is present.
  const pduLine = event?.pduInfo
    ? `PDU Information: ${event.pduInfo}${event.pduCode ? ` (Code: ${event.pduCode})` : ''}`
    : null;
  const missingItems = buildMissingItemsList(engagement);
  return {
    speakerName,
    speakerEmail: speaker?.email ?? '',
    speakerFirstName: orDefault(speaker?.preferredName, orDefault(speaker?.firstName, '[Speaker]')),
    talkTitle: orDefault(engagement?.talkTitle, '[Talk Title]'),
    eventName: orDefault(event?.name, '[Event Name]'),
    eventDate: event?.date
      ? new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : '[Date]',
    eventTime: orDefault(event?.startTime, '[Time]'),
    eventTimezone: event?.timeZone ?? '',
    // Every template that references this prints it on its own labeled line
    // ("• Format: {{eventFormat}}") with no surrounding {{#if}} guard, so a
    // missing value rendered as blank text after the colon in a real sent
    // email — bracket it like the other required-looking fields instead.
    eventFormat: orDefault(event?.format, '[Format]'),
    eventLocation: orDefault(event?.location, '[Location/Link]'),
    pduLine,
    nextDueDate: orDefault(engagement?.nextDueDate, '[Due Date]'),
    // The real link once one exists — previously this was always the literal
    // placeholder text below, even for an engagement with an active portal
    // link, so real emails never actually contained a working link.
    portalUrl: engagement?.activeTokenId
      ? `${window.location.origin}/portal/${engagement.activeTokenId}`
      : '[Portal URL — generate from Invite tab]',
    // null (falsy) when nothing is outstanding, so {{#if missingItems}} blocks
    // correctly omit themselves in the two timed confirmation templates.
    missingItems,
    // "Missing items summary" renders this unconditionally (no {{#if}} —
    // the whole point of that email is the list), so unlike missingItems
    // above it needs a real fallback instead of null, or sending it to a
    // speaker with nothing outstanding left literally "[missingItems]" in
    // the body.
    missingItemsSummary: missingItems ?? 'Everything looks complete — thank you!',
    ...buildSenderContext(sender),
  };
}

const TEMPLATES = {
  'Initial outreach': {
    subject: 'Speaking Opportunity — {{eventName}}',
    body: `Dear {{speakerFirstName}},

I hope this message finds you well. I'm reaching out on behalf of {{chapterName}} to invite you to consider speaking at our upcoming event, {{eventName}}, scheduled for {{eventDate}}.

We believe your expertise would be a tremendous value to our members. We'd love to discuss the possibility of you sharing your insights on {{talkTitle}}.

Could you let me know if you'd be interested and available? I'm happy to share more details about the event and expectations.

Thank you for your time, and I look forward to hearing from you.

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Invitation': {
    subject: 'Speaker Invitation — {{eventName}} on {{eventDate}}',
    body: `Dear {{speakerFirstName}},

Thank you for agreeing to speak at {{eventName}}! We are excited to have you.

Here are the confirmed details:
• Event: {{eventName}}
• Date: {{eventDate}}
• Time: {{eventTime}} {{eventTimezone}}
• Format: {{eventFormat}}
• Location: {{eventLocation}}
• Your session: {{talkTitle}}

To complete your speaker profile and submit your biography, headshot, and signed agreement, please use your personal portal link:

{{portalUrl}}

Please submit your materials by {{nextDueDate}}.

If you have any questions, don't hesitate to reach out.

Thank you again — we look forward to a great event!

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Confirmation': {
    subject: 'Speaking Engagement Confirmed — {{eventName}}',
    body: `Dear {{speakerFirstName}},

This email confirms all the details for your speaking engagement at {{eventName}}.

Event details:
• Date: {{eventDate}}
• Time: {{eventTime}} {{eventTimezone}}
• Format: {{eventFormat}}
• Location: {{eventLocation}}
• Your session: {{talkTitle}}

{{#if pduLine}}{{pduLine}}{{/if}}

We look forward to seeing you there. Please let us know if you have any questions or need assistance with logistics.

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Missing biography': {
    subject: 'Reminder: Biography Needed — {{eventName}}',
    body: `Dear {{speakerFirstName}},

Thank you again for speaking at {{eventName}} on {{eventDate}}.

We noticed we have not yet received your speaker biography. We need it by {{nextDueDate}} to promote your session to our members.

You can submit it through your personal portal:
{{portalUrl}}

Please don't hesitate to reach out if you need any assistance.

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Missing abstract': {
    subject: 'Reminder: Session Abstract Needed — {{eventName}}',
    body: `Dear {{speakerFirstName}},

We're looking forward to your presentation at {{eventName}} on {{eventDate}}!

We still need your session abstract for {{talkTitle}} by {{nextDueDate}} so we can finalize our event promotion.

Please submit it through your portal:
{{portalUrl}}

Thank you!

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Missing headshot': {
    subject: 'Reminder: Headshot Needed — {{eventName}}',
    body: `Dear {{speakerFirstName}},

Thank you for speaking at {{eventName}} on {{eventDate}}!

We still need a professional headshot from you by {{nextDueDate}} for our event promotions. Accepted formats are JPG, PNG, or WebP.

You can upload it through your portal:
{{portalUrl}}

Thank you!

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Missing agreement': {
    subject: 'Reminder: Speaker Agreement Needed — {{eventName}}',
    body: `Dear {{speakerFirstName}},

We're looking forward to having you at {{eventName}} on {{eventDate}}!

We have not yet received your signed speaker agreement. Please review, sign, and return it by {{nextDueDate}}.

You can upload the signed agreement through your portal:
{{portalUrl}}

If you have any questions about the agreement, please reach out.

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Due-date reminder': {
    subject: 'Action Needed: Materials Due {{nextDueDate}} — {{eventName}}',
    body: `Dear {{speakerFirstName}},

This is a friendly reminder that your outstanding materials for {{eventName}} are due by {{nextDueDate}}.

Please visit your portal to submit any remaining items:
{{portalUrl}}

If you have already submitted these materials, please disregard this message.

Thank you!

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Logistics confirmation': {
    subject: 'Logistics Confirmation — {{eventName}} on {{eventDate}}',
    body: `Dear {{speakerFirstName}},

We're just a short time away from {{eventName}} on {{eventDate}} and wanted to confirm final logistics for your session, {{talkTitle}}.

Event details:
• Date: {{eventDate}}
• Time: {{eventTime}} {{eventTimezone}}
• Format: {{eventFormat}}
• Location: {{eventLocation}}

Please let us know:
1. Will you be presenting from your own laptop or will you need ours?
2. Do you have any A/V or room-setup requirements?
3. Will you need anything else to ensure a smooth presentation?

We recommend arriving 15–30 minutes early for setup. Please reach out with any questions.

We look forward to seeing you!

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Thank-you / follow-up': {
    subject: 'Thank You for Speaking at {{eventName}}',
    body: `Dear {{speakerFirstName}},

On behalf of the entire chapter, thank you so much for presenting "{{talkTitle}}" at {{eventName}} on {{eventDate}}.

Your insights and expertise were greatly appreciated by our members, and the session was a true highlight of the event.

We would love to stay in touch and hope to have you speak with us again in the future. If you are interested, we'd be happy to add you to our speaker network.

Thank you again for your time and generosity.

With gratitude,
{{senderName}}
{{chapterName}}`,
  },

  'Request materials': {
    subject: 'Action Needed: Bio, Abstract & Photo — {{eventName}}',
    body: `Dear {{speakerFirstName}},

We're looking forward to your session, "{{talkTitle}}", at {{eventName}} on {{eventDate}}!

To finalize our event promotion, could you please send us the following at your earliest convenience:
• A short speaker biography
• A brief abstract for your session
• A professional headshot photo (JPG, PNG, or WebP)

You can submit all three through your personal portal:
{{portalUrl}}

Please aim to have these to us by {{nextDueDate}}.

Thank you!

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Missing items summary': {
    subject: 'Reminder: Outstanding Items — {{eventName}}',
    body: `Dear {{speakerFirstName}},

Thanks again for speaking at {{eventName}} on {{eventDate}}! As we get closer, here's a quick summary of what we're still waiting on:

{{missingItemsSummary}}

You can submit these through your personal portal at your earliest convenience:
{{portalUrl}}

Let us know if you have any questions.

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Confirmation — 1 month out': {
    subject: 'One Month to Go — {{eventName}} on {{eventDate}}',
    body: `Dear {{speakerFirstName}},

We're one month away from {{eventName}} on {{eventDate}}, and we're excited to have you presenting "{{talkTitle}}"!

Just confirming the details still work for you:
• Date: {{eventDate}}
• Time: {{eventTime}} {{eventTimezone}}
• Format: {{eventFormat}}
• Location: {{eventLocation}}
{{#if missingItems}}
A quick reminder — we're still waiting on:
{{missingItems}}
{{/if}}
Please let us know if anything has changed on your end. We're looking forward to it!

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Confirmation — 1 week out': {
    subject: 'One Week to Go — {{eventName}} on {{eventDate}}',
    body: `Dear {{speakerFirstName}},

Just one week until {{eventName}} on {{eventDate}}! We're confirming final details for your session, "{{talkTitle}}":
• Date: {{eventDate}}
• Time: {{eventTime}} {{eventTimezone}}
• Format: {{eventFormat}}
• Location: {{eventLocation}}
{{#if missingItems}}
A friendly reminder — we still need:
{{missingItems}}
{{/if}}
Please reach out with any last questions. We're looking forward to seeing you!

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Confirmation — day before': {
    subject: 'See You Tomorrow — {{eventName}}',
    body: `Dear {{speakerFirstName}},

Just a quick note to confirm we're all set for tomorrow at {{eventName}}!
• Time: {{eventTime}} {{eventTimezone}}
• Format: {{eventFormat}}
• Location: {{eventLocation}}

We recommend arriving 15–30 minutes early for setup. Please reach out if anything comes up.

Looking forward to a great session tomorrow!

Best regards,
{{senderName}}
{{chapterName}}`,
  },
};

/**
 * Generate a merged email draft.
 * @param {string} templateKey  One of the COMM_TYPES values
 * @param {object} engagement
 * @param {object} event
 * @param {object} speaker
 * @param {{name?: string, email?: string, chapterName?: string}} [sender]  The signed-in user sending this email
 * @returns {{ subject: string, body: string }}
 */
export function generateDraft(templateKey, engagement, event, speaker, sender) {
  const tpl = TEMPLATES[templateKey];
  if (!tpl) return { subject: '', body: '' };
  const ctx = buildContext(engagement, event, speaker, sender);
  return {
    subject: merge(tpl.subject, ctx),
    body: merge(tpl.body, ctx),
  };
}

// ── Pre-booking pipeline templates ────────────────────────────────────────
// A speaker in the pipeline (Potential/Contacted/Interested) predates any
// event/agreement — the merge context is just their own info, plus
// (optionally) the next upcoming event, so "Interest confirmed" can propose
// an actual date instead of a placeholder.

/** Build merge context for pre-booking pipeline templates. */
export function buildSpeakerPipelineContext(speaker, upcomingEvents = [], sender) {
  const nextEvent = upcomingEvents[0];
  return {
    prospectName: speaker
      ? `${orDefault(speaker.firstName, '[Name]')} ${orDefault(speaker.lastName, '')}`.trim()
      : '[Name]',
    prospectFirstName: orDefault(speaker?.preferredName, orDefault(speaker?.firstName, '[Name]')),
    prospectEmail: speaker?.email ?? '',
    topic: orDefault(speaker?.topic, '[Topic]'),
    nextEventName: orDefault(nextEvent?.name, '[Upcoming Event]'),
    nextEventDate: nextEvent?.date
      ? new Date(nextEvent.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : '[Date]',
    ...buildSenderContext(sender),
  };
}

const SPEAKER_PIPELINE_TEMPLATES = {
  'Initial outreach': {
    subject: 'Speaking Opportunity with {{chapterName}}',
    body: `Dear {{prospectFirstName}},

I hope this message finds you well. I'm reaching out on behalf of {{chapterName}} — we're always looking for engaging speakers to share their expertise with our members.
{{#if topic}}
We understand you may have insight into {{topic}}, and would love to explore having you speak on this or a related topic.
{{/if}}
Would you be open to a short conversation about a potential speaking engagement? I'd be happy to share more about our chapter, our audience, and upcoming opportunities.

Thank you for your time — I look forward to hearing from you.

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Follow-up nudge': {
    subject: 'Following Up — Speaking Opportunity',
    body: `Dear {{prospectFirstName}},

I wanted to follow up on my earlier note about a potential speaking opportunity with {{chapterName}}. I know schedules get busy, so I didn't want this to get lost in your inbox.

If you're still interested in speaking with us{{#if topic}} on {{topic}}{{/if}}, I'd love to find a time that works.

If now isn't a good time, no worries at all — just let me know and I'll follow up down the road.

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Interest confirmed': {
    subject: 'Great News — Let\'s Find a Date!',
    body: `Dear {{prospectFirstName}},

Thank you so much for your interest in speaking with our chapter — we're thrilled!

One event on our calendar that might be a great fit is {{nextEventName}} on {{nextEventDate}}. Would this work for your schedule, or would you prefer we look at a different date?

Once we've confirmed a date, I'll follow up with full event details and next steps for finalizing your session (title, abstract, bio, and agreement).

Looking forward to working with you!

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Polite decline': {
    subject: 'Thank You for Your Time',
    body: `Dear {{prospectFirstName}},

Thank you for taking the time to consider speaking with {{chapterName}}. I understand now isn't the right time, and I completely respect that.

I'd love to keep you in mind for a future opportunity, if you're open to it. Please don't hesitate to reach out if your availability or interest changes.

Thank you again, and best of luck with everything ahead.

Best regards,
{{senderName}}
{{chapterName}}`,
  },
};

/**
 * Generate a merged pre-booking pipeline email draft.
 * @param {string} templateKey  One of SPEAKER_COMM_TYPES
 * @param {object} speaker
 * @param {object[]} [upcomingEvents]
 */
export function generateSpeakerPipelineDraft(templateKey, speaker, upcomingEvents = [], sender) {
  const tpl = SPEAKER_PIPELINE_TEMPLATES[templateKey];
  if (!tpl) return { subject: '', body: '' };
  const ctx = buildSpeakerPipelineContext(speaker, upcomingEvents, sender);
  return {
    subject: merge(tpl.subject, ctx),
    body: merge(tpl.body, ctx),
  };
}

// ── Event-level templates ───────────────────────────────────────────────
// Addressed to every currently-engaged speaker for an event at once (the
// recipient list, not the template, is what makes these "event-level" —
// mailto: takes multiple comma-separated addresses). "Call for speakers" has
// no engaged speakers yet by definition, so its recipient list is left for
// the organizer to fill in manually.

/** Build merge context for event-level templates. */
export function buildEventContext(event, speakers = [], sender) {
  return {
    eventName: orDefault(event?.name, '[Event Name]'),
    eventDate: event?.date
      ? new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : '[Date]',
    eventTime: orDefault(event?.startTime, '[Time]'),
    eventTimezone: event?.timeZone ?? '',
    // Every template that references this prints it on its own labeled line
    // ("• Format: {{eventFormat}}") with no {{#if}} guard, so a missing
    // value rendered as blank text after the colon in a real sent email.
    eventFormat: orDefault(event?.format, '[Format]'),
    eventLocation: orDefault(event?.location, '[Location/Link]'),
    speakerNames: speakers.length
      ? speakers.map((s) => `${orDefault(s.firstName, '')} ${orDefault(s.lastName, '')}`.trim() || '[Speaker Name]').join(', ')
      : '[Speaker Name(s)]',
    ...buildSenderContext(sender),
  };
}

const EVENT_TEMPLATES = {
  'Logistics reminder': {
    subject: 'Logistics Reminder — {{eventName}} on {{eventDate}}',
    body: `Hello {{speakerNames}},

We're looking forward to {{eventName}} on {{eventDate}}! Here's a quick recap of the details:
• Date: {{eventDate}}
• Time: {{eventTime}} {{eventTimezone}}
• Format: {{eventFormat}}
• Location: {{eventLocation}}

Please let us know if you have any A/V or room-setup requirements, or if anything has changed on your end.

We recommend arriving 15–30 minutes early for setup.

Thank you, and see you soon!

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Cancelled/rescheduled notice': {
    subject: 'Important Update: {{eventName}}',
    body: `Hello {{speakerNames}},

We're writing to let you know that {{eventName}}, originally scheduled for {{eventDate}}, has been cancelled or rescheduled.

[Add details here: new date, or note that we'll follow up with a new date soon.]

We sincerely apologize for any inconvenience and appreciate your flexibility. We'll be in touch with updated details as soon as possible.

Thank you for your understanding.

Best regards,
{{senderName}}
{{chapterName}}`,
  },

  'Call for speakers': {
    subject: 'Speaking Opportunity — {{eventName}} on {{eventDate}}',
    body: `Hello,

We have an upcoming chapter event, {{eventName}}, on {{eventDate}}, and we're looking for a speaker to join us.

Event details:
• Date: {{eventDate}}
• Time: {{eventTime}} {{eventTimezone}}
• Format: {{eventFormat}}
• Location: {{eventLocation}}

Would you or someone you'd recommend be interested in speaking? We'd love to hear about topics you'd be excited to present.

Please let us know if you're interested, and thank you for considering!

Best regards,
{{senderName}}
{{chapterName}}`,
  },
};

/**
 * Generate a merged event-level email draft (addressed to multiple speakers at once).
 * @param {string} templateKey  One of EVENT_COMM_TYPES
 * @param {object} event
 * @param {object[]} [speakers]
 */
export function generateEventDraft(templateKey, event, speakers = [], sender) {
  const tpl = EVENT_TEMPLATES[templateKey];
  if (!tpl) return { subject: '', body: '' };
  const ctx = buildEventContext(event, speakers, sender);
  return {
    subject: merge(tpl.subject, ctx),
    body: merge(tpl.body, ctx),
  };
}

export { TEMPLATES, SPEAKER_PIPELINE_TEMPLATES, EVENT_TEMPLATES };
